/**
 * Opt-in WhatsApp (Simio) — landing mot de passe, écran 2.
 * Dépend de intl-tel-input (CDN) et du Worker Cloudflare (proxy Simio).
 */
class PasswordWhatsappOptin {
  #iti = null;

  #isSubmitting = false;

  #sendLabelDefault = '';

  constructor(wrapper) {
    this.wrapper = wrapper;
    if (!this.wrapper) return;

    this.bindUI();
    if (!this.form || !this.phoneInput) return;

    this.#initIntlTelInput();
    this.bindEvents();
  }

  bindUI() {
    this.form = this.wrapper.querySelector('.js-whatsapp-form');
    this.formContent = this.wrapper.querySelector('.js-whatsapp-form-content');
    this.phoneInput = this.wrapper.querySelector('.js-whatsapp-phone');
    this.sendBtn = this.wrapper.querySelector('.js-whatsapp-send');
    this.sendLabel = this.sendBtn?.querySelector('.js-btn-label') || this.sendBtn;
    this.errorBox = this.wrapper.querySelector('.js-whatsapp-error');
    this.successBox = this.wrapper.querySelector('.js-whatsapp-success');
    this.doneBtn = this.wrapper.querySelector('.js-whatsapp-done');
    this.honeypot = this.form?.querySelector('input[name="website"]');
    this.apiEndpoint = this.wrapper.dataset.apiEndpoint;
    this.phoneCountryEndpoint = this.wrapper.dataset.phoneCountryEndpoint;
    this.#sendLabelDefault = this.sendLabel?.textContent?.trim() || '';
  }

  bindEvents() {
    this.form?.addEventListener('submit', (event) => this.#handleSubmit(event));

    const sendWrapper = this.wrapper.querySelector('.js-whatsapp-send')?.closest('.cta-button');
    if (sendWrapper) {
      sendWrapper.addEventListener('click', (event) => {
        event.preventDefault();
        this.form?.dispatchEvent(new Event('submit', { cancelable: true }));
      });
    }

    this.doneBtn?.addEventListener('click', () => this.#handleDone());

    const pageRoot = this.wrapper.closest('.js-password-page');
    pageRoot?.addEventListener('password:whatsapp-reset', () => this.#resetForm());
  }

  #initIntlTelInput() {
    if (typeof window.intlTelInput !== 'function') return;

    this.#iti = window.intlTelInput(this.phoneInput, {
      initialCountry: 'auto',
      geoIpLookup: (success) => {
        const fallback = () =>
          fetch('https://ipapi.co/country/', { credentials: 'omit' })
            .then((r) => (r.ok ? r.text() : Promise.reject()))
            .then((code) => {
              const clean = code.trim().toLowerCase();
              return /^[a-z]{2}$/.test(clean) ? success(clean) : success('fr');
            })
            .catch(() => success('fr'));

        if (!this.phoneCountryEndpoint) {
          fallback();
          return;
        }

        fetch(this.phoneCountryEndpoint, { credentials: 'omit' })
          .then((response) => (response.ok ? response.json() : Promise.reject()))
          .then((data) => success((data.country || 'fr').toLowerCase()))
          .catch(() => fallback());
      },
      separateDialCode: true,
      showFlags: true,
      formatAsYouType: false,
      autoPlaceholder: 'aggressive',
      strictMode: false,
      countrySearch: true,
      countryNameLocale: (document.documentElement.lang || 'fr').split('-')[0],
      countryOrder: ['fr', 'gb', 'us', 'ae', 'it', 'es', 'de', 'jp'],
      fixDropdownWidth: false,
      useFullscreenPopup: false,
      dropdownContainer: document.body,
      i18n: {
        searchPlaceholder: this.wrapper.dataset.countrySearch || 'Search',
        zeroSearchResults: this.wrapper.dataset.countryNoResults || 'No results found',
      },
      loadUtils: () => import('https://cdn.jsdelivr.net/npm/intl-tel-input@25.0.0/build/js/utils.js'),
    });

    this.#stripPluginChrome();
    this.#injectDialChevron();
    this.#markDropdown();
    this.#bindDropdownPosition();
    this.#preventDropdownSearchAutofocus();

    this.phoneInput = this.#iti.getInput?.() || this.#iti.telInput || this.phoneInput;
    this.#bindPhoneFormatting();

    this.#iti.promise?.then(() => {
      this.phoneInput = this.#iti.getInput?.() || this.#iti.telInput || this.phoneInput;
      this.#stripPluginChrome();
      this.#injectDialChevron();
      this.#markDropdown();
      this.#applyMobileCountryLabels();
      this.#applyPhoneFormatting();
    }).catch(() => {
      this.#setError(this.wrapper.dataset.errorGeneric || '');
    });
  }

  #formatPhoneDigitsFallback(digits) {
    if (!digits) return '';

    const first = digits.charAt(0);
    const rest = digits.slice(1);
    const pairs = rest.match(/.{1,2}/g) || [];
    return pairs.length ? `${first} ${pairs.join(' ')}` : first;
  }

  #formatPhoneDigits(digits, countryCode) {
    if (!digits) return '';

    const utils = window.intlTelInput?.utils;
    if (utils?.formatNumberAsYouType && countryCode) {
      const formatted = utils.formatNumberAsYouType(digits, countryCode);
      // libphonenumber ne formate pas les numéros FR nationaux (sans 0) — fallback maison.
      if (formatted.replace(/\D/g, '') === digits && formatted === digits) {
        return this.#formatPhoneDigitsFallback(digits);
      }
      return formatted;
    }

    return this.#formatPhoneDigitsFallback(digits);
  }

  #getMaxNationalDigits(countryCode) {
    const utils = window.intlTelInput?.utils;
    if (!utils?.getExampleNumber || !countryCode) return 15;

    try {
      const example = utils.getExampleNumber(countryCode, true, utils.numberType.MOBILE);
      const nationalDigits = example?.replace(/\D/g, '') || '';
      return nationalDigits.length || 15;
    } catch {
      return 15;
    }
  }

  #getCursorDigitIndex(value, cursorPos) {
    return value.slice(0, cursorPos).replace(/\D/g, '').length;
  }

  #getCursorPosForDigitIndex(formattedValue, digitIndex) {
    if (digitIndex <= 0) return 0;

    let seen = 0;
    for (let i = 0; i < formattedValue.length; i++) {
      if (/\d/.test(formattedValue[i])) {
        seen += 1;
        if (seen >= digitIndex) return i + 1;
      }
    }

    return formattedValue.length;
  }

  #applyPhoneFormatting() {
    if (!this.phoneInput || !this.#iti) return;

    const countryCode = this.#iti.getSelectedCountryData()?.iso2;
    const maxDigits = this.#getMaxNationalDigits(countryCode);
    const digits = this.phoneInput.value.replace(/\D/g, '').slice(0, maxDigits);
    const formatted = this.#formatPhoneDigits(digits, countryCode);
    const digitIndex = this.#getCursorDigitIndex(this.phoneInput.value, this.phoneInput.selectionStart ?? 0);

    this.phoneInput.value = formatted;

    if (document.activeElement === this.phoneInput) {
      const nextPos = this.#getCursorPosForDigitIndex(formatted, Math.min(digitIndex, digits.length));
      this.phoneInput.setSelectionRange(nextPos, nextPos);
    }
  }

  #bindPhoneFormatting() {
    if (!this.phoneInput) return;

    if (this.phoneInput.dataset.formatBound === 'true') return;
    this.phoneInput.dataset.formatBound = 'true';

    this.phoneInput.addEventListener('input', () => this.#applyPhoneFormatting());
    this.phoneInput.addEventListener('countrychange', () => this.#applyPhoneFormatting());
  }

  #isDropdownOpen() {
    return (
      this.wrapper.querySelector('.iti__selected-country')?.getAttribute('aria-expanded') === 'true' ||
      !!document.querySelector('.js-password-whatsapp-iti-dropdown')
    );
  }

  #bindDropdownPosition() {
    const onScroll = (event) => {
      if (!this.#isDropdownOpen()) return;
      event.stopImmediatePropagation();
      this.#positionDropdown();
    };

    window.addEventListener('scroll', onScroll, true);
    this.#getPageRoot()?.querySelector('.js-password-overlay')?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', () => {
      if (this.#isDropdownOpen()) this.#positionDropdown();
    });

    window.visualViewport?.addEventListener('resize', () => {
      if (!this.#isDropdownOpen() || !this.#isMobileView()) return;
      this.#positionDropdown();
      this.#scrollPhoneIntoView();
    });

    this.wrapper.querySelector('.iti__selected-country')?.addEventListener('click', () => {
      [0, 50, 150].forEach((delay) => {
        setTimeout(() => {
          this.#stripPluginChrome();
          this.#markDropdown();
          this.#positionDropdown();
          this.#applyMobileCountryLabels();
          this.#bindDropdownSearchLabels();
          this.#bindDropdownSearchFocus();
        }, delay);
      });
    });
  }

  #getPageRoot() {
    return this.wrapper.closest('.js-password-page');
  }

  #scrollPhoneIntoView() {
    if (!this.#isMobileView()) return;

    this.wrapper.querySelector('.js-whatsapp-phone-wrapper')?.scrollIntoView({
      block: 'nearest',
      behavior: 'auto',
    });
  }

  #setDropdownSearchFocused(isFocused) {
    this.#getPageRoot()?.classList.toggle('js-password-dropdown-search-focused', isFocused);
  }

  #bindDropdownSearchFocus() {
    const dropdown = document.querySelector('.js-password-whatsapp-iti-dropdown');
    const searchInput = dropdown?.querySelector('.iti__search-input');
    if (!searchInput || searchInput.dataset.focusBound === 'true') return;

    searchInput.dataset.focusBound = 'true';

    searchInput.addEventListener('focus', () => {
      if (!this.#isMobileView()) return;

      this.#setDropdownSearchFocused(true);
      requestAnimationFrame(() => {
        this.#scrollPhoneIntoView();
        this.#positionDropdown();
      });
    });

    searchInput.addEventListener('blur', () => {
      this.#setDropdownSearchFocused(false);
    });
  }

  #preventDropdownSearchAutofocus() {
    // Sur mobile, intl-tel-input met le focus auto sur la recherche → clavier + saut d'écran.
    // L'astuce `readonly` laisse le focus s'appliquer sans ouvrir le clavier ; le tap utilisateur le réactive.
    document.addEventListener(
      'focusin',
      (event) => {
        const searchInput = event.target;
        if (!searchInput?.classList?.contains('iti__search-input')) return;
        if (!this.#isMobileView()) return;
        if (searchInput.dataset.userActivated === 'true') return;

        searchInput.setAttribute('readonly', 'readonly');
        searchInput.blur();

        if (searchInput.dataset.activationBound === 'true') return;
        searchInput.dataset.activationBound = 'true';
        searchInput.addEventListener('pointerdown', () => {
          searchInput.dataset.userActivated = 'true';
          searchInput.removeAttribute('readonly');
          searchInput.focus({ preventScroll: true });
        });
      },
      true
    );
  }

  #isMobileView() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  #applyMobileCountryLabels() {
    const dropdown = document.querySelector('.js-password-whatsapp-iti-dropdown');
    if (!dropdown) return;

    dropdown.classList.toggle('js-password-whatsapp-iti-dropdown--mobile-codes', this.#isMobileView());

    if (!this.#isMobileView()) return;

    dropdown.querySelectorAll('.iti__country').forEach((item) => {
      const code = item.getAttribute('data-country-code');
      const nameEl = item.querySelector('.iti__country-name');
      if (!code || !nameEl) return;
      nameEl.textContent = code.toUpperCase();
    });
  }

  #bindDropdownSearchLabels() {
    const dropdown = document.querySelector('.js-password-whatsapp-iti-dropdown');
    const searchInput = dropdown?.querySelector('.iti__search-input');
    if (!searchInput || searchInput.dataset.mobileLabelsBound === 'true') return;

    searchInput.dataset.mobileLabelsBound = 'true';
    searchInput.addEventListener('input', () => {
      requestAnimationFrame(() => this.#applyMobileCountryLabels());
    });
  }

  #getPageMargin() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--page-margin').trim();
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 16;
  }

  #getVisualViewportMetrics() {
    const viewport = window.visualViewport;
    if (!viewport) {
      return { top: 0, height: window.innerHeight };
    }

    return { top: viewport.offsetTop, height: viewport.height };
  }

  #positionDropdown() {
    const dropdown =
      this.#iti?.ui?.dropdownContent ||
      document.getElementById(`${this.#iti?.id}__dropdown-content`) ||
      document.querySelector('.js-password-whatsapp-iti-dropdown');

    const triggerStart = this.wrapper.querySelector('.iti__selected-country');
    const triggerEnd =
      this.wrapper.querySelector('.js-whatsapp-dial-chevron') ||
      this.wrapper.querySelector('.iti__selected-dial-code') ||
      triggerStart;

    if (!dropdown || !triggerStart || !triggerEnd) return;

    const rectStart = triggerStart.getBoundingClientRect();
    const rectEnd = triggerEnd.getBoundingClientRect();
    const margin = this.#getPageMargin();
    const viewportWidth = window.innerWidth;
    const maxWidth = viewportWidth - margin * 2;
    const selectorWidth = Math.max(rectEnd.right - rectStart.left, 1);
    const isMobile = this.#isMobileView();

    let resolvedDropdownWidth = isMobile
      ? 10 * 16
      : Math.min(Math.max(selectorWidth, 17 * 16), maxWidth);

    let left = rectStart.left;
    if (left + resolvedDropdownWidth > viewportWidth - margin) {
      left = viewportWidth - margin - resolvedDropdownWidth;
    }
    if (left < margin) {
      left = margin;
      resolvedDropdownWidth = Math.min(resolvedDropdownWidth, maxWidth);
    }

    const { top: viewportTop, height: viewportHeight } = this.#getVisualViewportMetrics();
    const dropdownHeight = dropdown.offsetHeight || 280;
    let top = rectStart.bottom + 4;

    if (isMobile && top + dropdownHeight > viewportTop + viewportHeight - margin) {
      top = Math.max(viewportTop + margin, viewportTop + viewportHeight - dropdownHeight - margin);
    }

    dropdown.style.position = 'fixed';
    dropdown.style.top = `${top}px`;
    dropdown.style.left = `${left}px`;
    dropdown.style.width = `${resolvedDropdownWidth}px`;
    dropdown.style.minWidth = `${resolvedDropdownWidth}px`;
    dropdown.style.maxWidth = isMobile ? `${resolvedDropdownWidth}px` : `${maxWidth}px`;
    dropdown.classList.toggle('js-password-whatsapp-iti-dropdown--mobile', isMobile);
  }

  #markDropdown() {
    const dropdown =
      this.#iti?.ui?.dropdownContent ||
      document.getElementById(`${this.#iti?.id}__dropdown-content`) ||
      document.querySelector('.iti__dropdown-content');

    if (!dropdown) return;

    dropdown.classList.add('js-password-whatsapp-iti-dropdown');
  }

  #stripPluginChrome() {
    this.wrapper.querySelectorAll('.iti__arrow').forEach((el) => {
      el.setAttribute('hidden', '');
    });
  }

  #injectDialChevron() {
    const selectedCountry = this.wrapper.querySelector('.iti__selected-country');
    if (!selectedCountry || selectedCountry.querySelector('.js-whatsapp-dial-chevron')) return;

    const chevron = document.createElement('span');
    chevron.className = 'js-whatsapp-dial-chevron';
    chevron.setAttribute('aria-hidden', 'true');
    selectedCountry.appendChild(chevron);
  }

  #setError(message) {
    if (!this.errorBox) return;
    this.errorBox.textContent = message;
    message
      ? this.errorBox.removeAttribute('hidden')
      : this.errorBox.setAttribute('hidden', '');
  }

  #setSubmitting(isSubmitting) {
    this.#isSubmitting = isSubmitting;
    if (this.sendBtn) this.sendBtn.disabled = isSubmitting;
    if (this.sendLabel) {
      this.sendLabel.textContent = isSubmitting
        ? this.wrapper.dataset.sendingLabel || '…'
        : this.#sendLabelDefault;
    }
  }

  #showSuccess() {
    this.formContent?.setAttribute('hidden', '');
    this.successBox?.removeAttribute('hidden');
    this.#setError('');
  }

  async #handleSubmit(event) {
    event.preventDefault();
    if (this.#isSubmitting) return;

    this.#setError('');
    this.successBox?.setAttribute('hidden', '');

    if (this.honeypot && this.honeypot.value.trim() !== '') return;

    if (!this.#iti) {
      this.#setError(this.wrapper.dataset.errorGeneric || '');
      return;
    }

    this.#setSubmitting(true);

    try {
      await this.#iti.promise;

      const country = this.#iti.getSelectedCountryData();
      if (!country?.iso2 || !country?.dialCode || !this.#iti.isValidNumber()) {
        this.#setError(this.wrapper.dataset.errorPhone || '');
        return;
      }

      const number = this.#iti.getNumber();
      if (!/^\+[1-9]\d{7,14}$/.test(number)) {
        this.#setError(this.wrapper.dataset.errorPhone || '');
        return;
      }

      const phone = number.slice(1);
      const locale = (document.documentElement.lang || '').trim().toLowerCase().split('-')[0];
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone,
          locale,
          consent: true,
          website: this.honeypot ? this.honeypot.value : '',
        }),
      });

      const result = await response.json();
      if (!response.ok || result.success !== true) {
        throw new Error(result.error || 'Submission failed');
      }

      this.#showSuccess();
    } catch {
      this.#setError(this.wrapper.dataset.errorGeneric || '');
    } finally {
      this.#setSubmitting(false);
    }
  }

  #handleDone() {
    const pageRoot = this.wrapper.closest('.js-password-page');
    pageRoot?.dispatchEvent(new CustomEvent('password:close'));
  }

  #resetForm() {
    this.formContent?.removeAttribute('hidden');
    this.successBox?.setAttribute('hidden', '');
    this.#setError('');
    this.#setDropdownSearchFocused(false);
    if (this.phoneInput) this.phoneInput.value = '';
    this.#iti?.setNumber('');
    this.#setSubmitting(false);
  }
}

const initPasswordWhatsappOptin = () => {
  document.querySelectorAll('.js-password-join-whatsapp').forEach((wrapper) => {
    if (wrapper.dataset.initialized === 'true') return;
    wrapper.dataset.initialized = 'true';
    new PasswordWhatsappOptin(wrapper);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPasswordWhatsappOptin);
} else {
  initPasswordWhatsappOptin();
}
